import React, { useState, useEffect } from 'react';
import { 
  Database, RefreshCw, Settings, Check, AlertCircle, X, 
  Link2, Shield, Info, ChevronDown, ChevronRight, Globe,
  Key, Lock, BarChart2, Clock, Calendar, Users, Zap,
  Plus, Loader2
} from 'lucide-react';
import { Button } from '../ui/Button';
import { fetchCrmConnections, testCrmConnection, saveCrmConnection, deleteCrmConnection } from '../../lib/crm/service';
import { CrmConnectionConfig, ConnectionStatus } from '../../lib/crm/types';
import { crmProviders, getCrmProvider, getDefaultConnectionConfig } from '../../lib/crm/providers';
import toast from 'react-hot-toast';
import CrmConnectionForm from './CrmConnectionForm';
import CrmFieldMappingEditor from './CrmFieldMappingEditor';

interface CrmConnectionManagerProps {
  onConnectionChange?: () => void;
}

const CrmConnectionManager: React.FC<CrmConnectionManagerProps> = ({ onConnectionChange }) => {
  const [connections, setConnections] = useState<CrmConnectionConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTesting, setIsTesting] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState<string | null>(null);
  const [showMappingModal, setShowMappingModal] = useState<string | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [connectionStatuses, setConnectionStatuses] = useState<Record<string, ConnectionStatus>>({});

  useEffect(() => {
    loadConnections();
  }, []);

  const loadConnections = async () => {
    try {
      setIsLoading(true);
      const connections = await fetchCrmConnections();
      setConnections(connections);
      
      // Initialize connection statuses
      const statuses: Record<string, ConnectionStatus> = {};
      connections.forEach(connection => {
        statuses[connection.id] = {
          id: crypto.randomUUID(),
          connectionId: connection.id,
          status: 'pending'
        };
      });
      setConnectionStatuses(statuses);
      
      // Test each connection
      for (const connection of connections) {
        if (connection.enabled) {
          try {
            const status = await testCrmConnection(connection);
            setConnectionStatuses(prev => ({
              ...prev,
              [connection.id]: status
            }));
          } catch (error) {
            console.error(`Error testing connection ${connection.id}:`, error);
            setConnectionStatuses(prev => ({
              ...prev,
              [connection.id]: {
                id: crypto.randomUUID(),
                connectionId: connection.id,
                status: 'error',
                lastError: {
                  type: 'UNKNOWN',
                  message: error.message || 'Unknown error',
                  timestamp: new Date().toISOString()
                }
              }
            }));
          }
        }
      }
    } catch (error) {
      console.error('Error loading connections:', error);
      toast.error('Failed to load CRM connections');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestConnection = async (connectionId: string) => {
    try {
      setIsTesting(connectionId);
      const connection = connections.find(c => c.id === connectionId);
      if (!connection) throw new Error('Connection not found');
      
      const status = await testCrmConnection(connection);
      setConnectionStatuses(prev => ({
        ...prev,
        [connectionId]: status
      }));
      
      if (status.status === 'connected') {
        toast.success('Connection test successful');
      } else {
        toast.error(`Connection test failed: ${status.lastError?.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error(`Error testing connection ${connectionId}:`, error);
      toast.error(`Connection test failed: ${error.message || 'Unknown error'}`);
      
      setConnectionStatuses(prev => ({
        ...prev,
        [connectionId]: {
          id: crypto.randomUUID(),
          connectionId,
          status: 'error',
          lastError: {
            type: 'UNKNOWN',
            message: error.message || 'Unknown error',
            timestamp: new Date().toISOString()
          }
        }
      }));
    } finally {
      setIsTesting(null);
    }
  };

  const handleAddConnection = async (config: Partial<CrmConnectionConfig>) => {
    try {
      // Generate a new ID if not provided
      if (!config.id) {
        config.id = crypto.randomUUID();
      }
      
      // Save the connection
      await saveCrmConnection(config as CrmConnectionConfig);
      
      // Reload connections
      await loadConnections();
      
      // Notify parent component
      if (onConnectionChange) {
        onConnectionChange();
      }
      
      // Close the modal
      setShowAddModal(false);
      setSelectedProvider(null);
      
      toast.success('CRM connection added successfully');
    } catch (error) {
      console.error('Error adding connection:', error);
      toast.error(`Failed to add connection: ${error.message || 'Unknown error'}`);
    }
  };

  const handleEditConnection = async (config: Partial<CrmConnectionConfig>) => {
    try {
      // Save the connection
      await saveCrmConnection(config as CrmConnectionConfig);
      
      // Reload connections
      await loadConnections();
      
      // Notify parent component
      if (onConnectionChange) {
        onConnectionChange();
      }
      
      // Close the modal
      setShowEditModal(null);
      
      toast.success('CRM connection updated successfully');
    } catch (error) {
      console.error('Error updating connection:', error);
      toast.error(`Failed to update connection: ${error.message || 'Unknown error'}`);
    }
  };

  const handleDeleteConnection = async (connectionId: string) => {
    try {
      // Confirm deletion
      if (!confirm('Are you sure you want to delete this connection? This action cannot be undone.')) {
        return;
      }
      
      // Delete the connection
      await deleteCrmConnection(connectionId);
      
      // Reload connections
      await loadConnections();
      
      // Notify parent component
      if (onConnectionChange) {
        onConnectionChange();
      }
      
      toast.success('CRM connection deleted successfully');
    } catch (error) {
      console.error(`Error deleting connection ${connectionId}:`, error);
      toast.error(`Failed to delete connection: ${error.message || 'Unknown error'}`);
    }
  };

  const getStatusBadge = (status: ConnectionStatus['status']) => {
    switch (status) {
      case 'connected':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <Check className="h-3 w-3 mr-1" />
          Connected
        </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
          Pending
        </span>;
      case 'error':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <AlertCircle className="h-3 w-3 mr-1" />
          Error
        </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <X className="h-3 w-3 mr-1" />
          Disconnected
        </span>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">CRM Connections</h3>
        <Button
          onClick={() => setShowAddModal(true)}
          className="py-1.5 px-3"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Connection
        </Button>
      </div>
      
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 text-primary animate-spin" />
        </div>
      ) : connections.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <Database className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No CRM Connections</h3>
          <p className="text-gray-500 mb-6">
            Connect your CRM system to synchronize contacts, opportunities, and more.
          </p>
          <Button
            onClick={() => setShowAddModal(true)}
            className="mx-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Connection
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {connections.map((connection) => {
            const provider = getCrmProvider(connection.provider);
            const status = connectionStatuses[connection.id];
            
            return (
              <div
                key={connection.id}
                className={`border rounded-lg p-4 ${
                  status?.status === 'connected' ? 'border-green-300 bg-green-50/30' :
                  status?.status === 'error' ? 'border-red-300 bg-red-50/30' :
                  'border-gray-200'
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-lg">
                      {provider?.logo ? (
                        <img 
                          src={provider.logo} 
                          alt={provider.name} 
                          className="max-w-full max-h-full object-contain"
                          onError={(e) => {
                            // Fallback to text if image fails to load
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            target.parentElement!.textContent = provider.name.charAt(0);
                            target.parentElement!.className = "w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-lg";
                          }}
                        />
                      ) : (
                        <Database className="h-6 w-6 text-gray-500" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-gray-900">{connection.name}</h4>
                        {status && getStatusBadge(status.status)}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{provider?.description || 'CRM Connection'}</p>
                      {status?.lastSyncTime && (
                        <p className="text-xs text-gray-500 mt-1">
                          Last synced: {new Date(status.lastSyncTime).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTestConnection(connection.id)}
                      disabled={isTesting === connection.id}
                      className="py-1 px-2"
                    >
                      {isTesting === connection.id ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          Testing...
                        </>
                      ) : (
                        <>
                          <Link2 className="h-4 w-4 mr-1" />
                          Test
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowMappingModal(connection.id)}
                      className="py-1 px-2"
                    >
                      <Settings className="h-4 w-4 mr-1" />
                      Mappings
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowEditModal(connection.id)}
                      className="py-1 px-2"
                    >
                      <Settings className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteConnection(connection.id)}
                      className="py-1 px-2 text-red-600 hover:text-red-700 hover:border-red-600"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Remove
                    </Button>
                  </div>
                </div>
                
                {status?.status === 'error' && status.lastError && (
                  <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-start">
                      <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-2" />
                      <div>
                        <p className="text-sm font-medium text-red-800">Connection Error</p>
                        <p className="text-sm text-red-700 mt-1">{status.lastError.message}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      {/* Add Connection Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-medium">Add CRM Connection</h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setSelectedProvider(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-4 max-h-[calc(90vh-8rem)] overflow-y-auto">
              {selectedProvider ? (
                <CrmConnectionForm
                  initialConfig={getDefaultConnectionConfig(selectedProvider)}
                  onSave={handleAddConnection}
                  onCancel={() => setSelectedProvider(null)}
                />
              ) : (
                <div>
                  <p className="text-gray-600 mb-6">
                    Select a CRM provider to connect with AdvisorGrow:
                  </p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {crmProviders.map((provider) => (
                      <div
                        key={provider.id}
                        className="border rounded-lg p-4 hover:border-primary hover:bg-primary/5 cursor-pointer transition-colors h-full flex flex-col"
                        onClick={() => setSelectedProvider(provider.id)}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg flex-shrink-0">
                            {provider.logo ? (
                              <img 
                                src={provider.logo} 
                                alt={provider.name} 
                                className="max-w-full max-h-full object-contain"
                                onError={(e) => {
                                  // Fallback to text if image fails to load
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                  target.parentElement!.textContent = provider.name.charAt(0);
                                  target.parentElement!.className = "w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-lg";
                                }}
                              />
                            ) : (
                              <Database className="h-5 w-5 text-gray-500" />
                            )}
                          </div>
                          <h4 className="font-medium text-gray-900">{provider.name}</h4>
                        </div>
                        <p className="text-sm text-gray-600 mb-3 flex-grow">{provider.description}</p>
                        <div className="flex flex-wrap gap-1 mt-auto">
                          {provider.authMethod === 'oauth2' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
                              <Lock className="h-3 w-3 mr-1" />
                              OAuth 2.0
                            </span>
                          )}
                          {provider.authMethod === 'apikey' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-100 text-green-800">
                              <Key className="h-3 w-3 mr-1" />
                              API Key
                            </span>
                          )}
                          {provider.authMethod === 'dual' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-800">
                              <Shield className="h-3 w-3 mr-1" />
                              Multiple Auth
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Connection Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-medium">Edit CRM Connection</h3>
              <button
                onClick={() => setShowEditModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-4 max-h-[calc(90vh-8rem)] overflow-y-auto">
              <CrmConnectionForm
                initialConfig={connections.find(c => c.id === showEditModal) || {}}
                onSave={handleEditConnection}
                onCancel={() => setShowEditModal(null)}
                isEditing
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Field Mapping Modal */}
      {showMappingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-medium">Field Mappings</h3>
              <button
                onClick={() => setShowMappingModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-4 max-h-[calc(90vh-8rem)] overflow-y-auto">
              <CrmFieldMappingEditor
                connectionId={showMappingModal}
                onClose={() => setShowMappingModal(null)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrmConnectionManager;